import {
  AlertIcon,
  Banner,
  Box,
  Button,
  EditIcon,
  Flex,
  Image,
  Loading,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import { useIsMutating } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import {
  MAX_IMAGE_SIZE,
  VALID_IMAGE_MIME_TYPES,
  businessPaths,
} from '@jane/business-admin/util';
import { ImagePreview } from '@jane/shared/util';

import { ModalPageWrapper } from './components/ModalPageWrapper';

import styles from './PremiumLogo.module.css';

const imgTooLarge = (file: File) =>
  file.size <= MAX_IMAGE_SIZE || 'Your file exceeded the 2mb limit.';
const imgWrongFormat = (file: File) =>
  VALID_IMAGE_MIME_TYPES.includes(file.type) ||
  'Your file type is not supported. Try using .jpg or .png files.';

export const PremiumLogo = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const bloomMenuQuery = useFetchCurrentBloomMenu();
  const { mutate: updateBloomMenu, isLoading } = useUpdateBloomMenu(
    bloomMenuQuery.data?.id
  );

  const isUploading = useIsMutating({
    mutationKey: ['update-bloom-menu', bloomMenuQuery.data?.id],
  });

  const [imageErrors, setImageErrors] = useState<(string | boolean)[]>();

  const formMethods = useForm({
    defaultValues: {
      logo: bloomMenuQuery.data?.logo?.large ?? '',
    },
  });
  const { control, setValue, reset } = formMethods;
  const imageUrl = useWatch({ control, name: 'logo' });

  useEffect(() => {
    if (
      bloomMenuQuery.data?.logo?.large &&
      !bloomMenuQuery.isFetching &&
      !imageUrl
    ) {
      reset({
        logo: bloomMenuQuery.data.logo.large,
      });
    }
  }, [
    bloomMenuQuery.data?.logo?.large,
    bloomMenuQuery.isFetching,
    imageUrl,
    reset,
  ]);

  const onDrop = useCallback(
    async ([file]: File[]) => {
      const errorMessages = [imgTooLarge(file), imgWrongFormat(file)].filter(
        (result) => result !== true
      );

      if (errorMessages.length > 0) {
        setImageErrors(errorMessages);
      } else {
        const image = await ImagePreview.parse(file);
        setValue('logo', image.url, { shouldDirty: true });
        setImageErrors([]);
      }
    },
    [setValue]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
  });

  const publish = useCallback(() => {
    updateBloomMenu(
      {
        logo: imageUrl,
      },
      {
        onError: () => {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
        },
        onSuccess: () => {
          toast.add({
            label: 'Menu logo updated!',
            variant: 'success',
          });
          navigate(businessPaths.premium());
        },
      }
    );
  }, [imageUrl, toast, updateBloomMenu, navigate]);

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isLoading || !!isUploading}
      onSubmit={publish}
      title="Logo"
    >
      <Flex
        p={40}
        gap={24}
        flexDirection="column"
        alignItems="center"
        alignContent="center"
      >
        <Typography width={'100%'}>
          Upload your logo that will appear in the header of your homepage.
        </Typography>

        {imageErrors?.map((error, i) => (
          <Banner
            full
            icon={<AlertIcon />}
            label={error}
            variant="error"
            onDismiss={() =>
              setImageErrors(imageErrors.filter((e) => e !== error))
            }
            key={i}
          />
        ))}

        <div {...getRootProps()}>
          {imageUrl ? (
            <Box
              className={styles['imageContainer']}
              borderRadius="sm"
              border="grays-light"
              mb={16}
              p={16}
              position="relative"
            >
              <Image
                key={imageUrl}
                src={imageUrl}
                altText="Menu Logo"
                height="264px"
                width="264px"
                borderRadius="16px"
                objectFit="contain"
              />
              <div className={styles['editButtonContainer']}>
                <Button.Icon
                  label="Edit"
                  variant="secondary"
                  icon={<EditIcon />}
                  onClick={open}
                />
                {/* Hidden input for form value (image URL string) */}
                <Controller
                  control={control}
                  name="logo"
                  render={({ field }) => <input type="hidden" {...field} />}
                />
                {/* File input for dropzone */}
                <input {...getInputProps()} />
              </div>
            </Box>
          ) : (
            <Box
              p={16}
              mb={16}
              background="grays-ultralight"
              borderRadius="sm"
              height={264}
              width={264}
            >
              {bloomMenuQuery.isLoading ||
              (bloomMenuQuery.isFetching && !imageUrl) ? (
                <Flex
                  position="relative"
                  justifyContent="center"
                  flexDirection="column"
                  alignContent="center"
                  height="100%"
                >
                  <Loading />
                </Flex>
              ) : (
                <Flex
                  flexDirection="column"
                  alignItems="center"
                  alignContent="center"
                  height="100%"
                  justifyContent="center"
                >
                  <Button
                    mb={24}
                    label="Upload from computer"
                    type="button"
                    variant="secondary"
                    onClick={open}
                  />
                  {/* Hidden input for form value (image URL string) */}
                  <Controller
                    control={control}
                    name="logo"
                    render={({ field }) => <input type="hidden" {...field} />}
                  />
                  {/* File input for dropzone */}
                  <input {...getInputProps()} />
                  <Typography color="grays-mid" variant="body" mb={8}>
                    .jpg or .png
                  </Typography>
                  <Typography color="grays-mid" variant="body">
                    2mb file limit
                  </Typography>
                </Flex>
              )}
            </Box>
          )}
        </div>
      </Flex>
    </ModalPageWrapper>
  );
};
